import React, { useState, useMemo } from 'react'
import omit from 'lodash/omit'
import {
   Typography,
   Button,
   FormControl,
   InputLabel,
   Select,
   OutlinedInput,
   FormHelperText,
   MenuItem,
   TextField
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TooltipInputError from '../../toolTip/ToolTipInputError'
import { Autocomplete } from '@material-ui/lab'
import Utils from 'utils/utils'
import TRANSLATE, { OPERACIONES_MODULOS, VALUES } from 'utils/constants'
import { useHasOperation } from 'hooks/useHasOperation'

const useStyles = makeStyles(theme => ({
   formContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '95%',
      margin: '0 auto 10px'
   },
   formControl: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: 14,
      '&:not(:first-child)': {
         marginTop: 20
      }
   },
   formControlTextArea: {
      width: '100%',
      minWidth: 120,
      textAlign: 'initial',
      fontSize: '14px',
      '&:not(:first-child)': {
         marginTop: 20
      }
   },
   button: {
      width: '100%',
      height: 40,
      marginTop: 20,
      color: 'white',
      background: '#3264DC'
   },
   formCleanButton: {
      color: '#3264DC',
      border: '1px solid #3264DC',
      fontWeight: 'bold',
      marginTop: 20,
      width: '100%'
   },
   helperText: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      fontSize: 11
   },
   selectEmpty: {
      marginTop: theme.spacing(2)
   },
   disabledButton: {
      backgroundColor: '#12B3F9!important',
      opacity: '0.3',
      color: '#fff!important'
   },
   MuiFormHelperText: {
      root: {
         margin: '0 !important'
      },
      margin: '0 !important'
   },
   chips: {
      display: 'flex',
      flexWrap: 'wrap'
   },
   chip: {
      margin: 2
   },
   multipleSelectLabel: {
      backgroundColor: 'white'
   },
   titleOne: {
      margin: '20px 0 15px 9px',
      fontStyle: 'normal',
      fontSize: 14,
      alignSelf: 'start',
      color: '#8A8FAB',
      fontWeight: 400
   }
}))

const NO_MORE_THAN_50_CHARACTERS = Utils.noMoreThanNCharacters(50)
const NO_MORE_THAN_255_CHARACTERS = Utils.noMoreThanNCharacters(255)

const INITIAL_MODULE_FORM_VALIDATION = {
   name: false,
   status: false,
   description: false,
   application: false,
   link: false
}

const validRequired = value => !value || value === ''

const validationsFields = {
   name: value => value.length > 150 || validRequired(value),
   // value.length > 50 || !REGEXP.VALIDATION_NAME.exec(value) || validRequired(value),
   description: value => value.length > 255 || validRequired(value),
   link: validRequired,
   application: validRequired,
   status: validRequired
}

const ModuleForm = ({ formData, setForm, applications, actionSubmit, formBehavior, cleanCallback }) => {
   const classes = useStyles()

   const CONSULTAR_MODULOS = useHasOperation(OPERACIONES_MODULOS.CONSULTAR_MODULOS)
   const REGISTRAR_MODULOS = useHasOperation(OPERACIONES_MODULOS.REGISTRAR_MODULOS)

   const isSearch = useMemo(() => formBehavior === VALUES.FORM_BEHAVIOR.SEARCH, [formBehavior])

   const [invalid, setInvalid] = useState(INITIAL_MODULE_FORM_VALIDATION)

   const cleanForm = () => {
      cleanCallback()
      setInvalid(INITIAL_MODULE_FORM_VALIDATION)
   }

   const validateFields = (name, value) => {
      const isInvalidFn = name in validationsFields ? validationsFields[name] : () => {}
      const isInvalid = isInvalidFn(value)
      if (isInvalid) {
         setInvalid(inv => ({
            ...inv,
            [name]: true
         }))
      } else if (invalid[name]) {
         setInvalid(inv => ({
            ...inv,
            [name]: false
         }))
      }
   }

   const addValuesToMemory = e => {
      const { name, value } = e.target
      if (!isSearch) {
         validateFields(name, value)
      }
      setForm(prevFormData => ({
         ...prevFormData,
         [name]: value
      }))
   }

   const registerModules = type => {
      let isInvalidField = false
      const formFields =
         type === VALUES.FORM_BEHAVIOR.REGISTER
            ? omit(INITIAL_MODULE_FORM_VALIDATION, 'status')
            : INITIAL_MODULE_FORM_VALIDATION
      const newValidations = {}
      for (const key in formFields) {
         const isInvalid = validationsFields[key](formData[key])
         newValidations[key] = isInvalid
         if (isInvalid && !isInvalidField) {
            isInvalidField = isInvalid
         }
      }
      if (isInvalidField) {
         setInvalid(prev => {
            return { ...prev, ...newValidations }
         })
      } else {
         actionSubmit(type)
      }
   }

   const actionSubmitEvent = e => {
      e.preventDefault()
      switch (formBehavior) {
         case VALUES.FORM_BEHAVIOR.REGISTER: {
            registerModules(VALUES.FORM_BEHAVIOR.REGISTER)
            break
         }
         case VALUES.FORM_BEHAVIOR.SEARCH: {
            actionSubmit(VALUES.FORM_BEHAVIOR.SEARCH)
            break
         }
         case VALUES.FORM_BEHAVIOR.EDIT: {
            registerModules(VALUES.FORM_BEHAVIOR.EDIT)
            break
         }
         default:
            break
      }
   }

   return (
      <div className={classes.formContainer}>
         <Typography className={classes.titleOne}>
            <b> Completa los campos </b>{' '}
         </Typography>

         <form className={classes.formContainer}>
            <FormControl variant="outlined" size="small" className={classes.formControl} error={invalid.name}>
               <InputLabel htmlFor="outlined-adornment-amount">
                  {`Nombre del Módulo ${!isSearch ? '*' : ''}`}
               </InputLabel>
               <OutlinedInput
                  error={invalid.name}
                  value={formData.name}
                  onChange={e => {
                     const value = e.target.value.toUpperCase()
                     addValuesToMemory({
                        target: { name: 'name', value }
                     })
                     addValuesToMemory({
                        target: {
                           name: 'link',
                           value: Utils.buildUrlFromText(value)
                        }
                     })
                  }}
                  name="name"
                  size="small"
                  label="Nombre del Módulo "
                  inputProps={{ maxLength: 50 }}
                  endAdornment={
                     (invalid.name && <TooltipInputError title={'Ingrese un nombre valido'} />) ||
                     (formData.name.length === 50 && <TooltipInputError title={NO_MORE_THAN_50_CHARACTERS} />)
                  }
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.name && 'El campo nombre del Módulo es obligatorio'}
               </FormHelperText>
            </FormControl>

            {!isSearch && (
               <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.formControlTextArea}
                  error={invalid.description}
               >
                  <InputLabel htmlFor="outlined-adornment-amount">
                     {`Descripción del Módulo ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <OutlinedInput
                     error={invalid.description}
                     value={formData.description}
                     onChange={addValuesToMemory}
                     label="Descripción del Módulo  "
                     name="description"
                     endAdornment={
                        (invalid?.description && <TooltipInputError title={'Ingrese una descripcion valida'} />) ||
                        (formData?.description?.length === 255 && (
                           <TooltipInputError title={NO_MORE_THAN_255_CHARACTERS} />
                        ))
                     }
                     multiline
                     rows={4}
                     inputProps={{ maxLength: 255 }}
                  />
                  <FormHelperText className={classes.helperText}>
                     {invalid.description && 'El campo Descripción del Módulo es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            <FormControl size="small" variant="outlined" className={classes.formControl} error={invalid.application}>
               <Autocomplete
                  size="small"
                  options={applications}
                  getOptionLabel={option => option.name}
                  renderOption={option => <span key={option.id}>{option.name}</span>}
                  value={formData.application}
                  onChange={(e, newValue) => {
                     addValuesToMemory({
                        target: {
                           name: 'application',
                           value: newValue
                        }
                     })
                  }}
                  renderInput={params => (
                     <TextField
                        {...params}
                        label={`Nombre de Aplicación ${!isSearch ? '*' : ''}`}
                        variant="outlined"
                        value={''}
                        error={invalid.application}
                     />
                  )}
               />
               <FormHelperText className={classes.helperText}>
                  {invalid.application && 'El campo Nombre de la Aplicación es obligatorio'}
               </FormHelperText>
            </FormControl>

            {!isSearch && (
               <FormControl variant="outlined" size="small" className={classes.formControl} error={invalid.link}>
                  <InputLabel htmlFor="outlined-adornment-amount">
                     {`Url del Módulo ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <OutlinedInput
                     error={invalid.link}
                     value={formData.link}
                     onChange={e => {
                        addValuesToMemory({
                           target: {
                              name: 'link',
                              value: Utils.buildUrlFromText(e.target.value)
                           }
                        })
                     }}
                     name="link"
                     size="small"
                     label="Url del Módulo "
                     endAdornment={invalid.link && <TooltipInputError title={'ingrese una url válida'} />}
                  />
                  <FormHelperText className={classes.helperText}>
                     {invalid.link && 'El campo Url del Módulo es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            {(isSearch || formBehavior === VALUES.FORM_BEHAVIOR.EDIT) && (
               <FormControl variant="outlined" className={classes.formControl} size="small" error={invalid.status}>
                  <InputLabel htmlFor="outlined-type-native-simple">
                     {`Estado del Módulo ${!isSearch ? '*' : ''}`}
                  </InputLabel>
                  <Select
                     value={formData.status}
                     onChange={addValuesToMemory}
                     size="small"
                     name="status"
                     label="Estado del Módulo  "
                     inputProps={{
                        name: 'status',
                        id: 'outlined-type-native-simple'
                     }}
                  >
                     <MenuItem aria-label="None" value={''} />
                     {VALUES.STATUS_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                           {option.label}
                        </MenuItem>
                     ))}
                  </Select>
                  <FormHelperText className={classes.helperText}>
                     {invalid.status && 'El campo Estado del Módulo es obligatorio'}
                  </FormHelperText>
               </FormControl>
            )}

            <Button
               type="submit"
               fullWidth
               variant="contained"
               color="primary"
               className={classes.button}
               onClick={actionSubmitEvent}
               disabled={formBehavior === VALUES.FORM_BEHAVIOR.SEARCH ? !CONSULTAR_MODULOS : !REGISTRAR_MODULOS}
            >
               {formBehavior === VALUES.FORM_BEHAVIOR.EDIT
                  ? TRANSLATE.BUTTONS.EDIT
                  : isSearch
                  ? TRANSLATE.BUTTONS.SEARCH
                  : TRANSLATE.BUTTONS.SAVE}
            </Button>

            <Button
               classes={{
                  root: classes.formCleanButton
               }}
               variant="outlined"
               onClick={cleanForm}
            >
               {TRANSLATE.BUTTONS.CLEAN}
            </Button>
         </form>
      </div>
   )
}

export default ModuleForm
